import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Business as BusinessIcon, Schedule as ScheduleIcon } from '@mui/icons-material';
import Layout from '../components/Layout';
import { useFilter } from '../contexts/FilterContext';
import { getRefreshPaperStyles, getRefreshTableStyles } from '../utils/refreshAnimation';
import { tenantService } from '../services/tenantService';
import type { Tenant } from '../types/tenant';
import { formatDateToTR } from '../utils/dateUtils';

const Tenants = () => {
  const { refreshTrigger, autoRefresh, isRefreshing } = useFilter();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const navigate = useNavigate();

  const loadTenants = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await tenantService.getAll();
      setTenants(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Şirket grupları yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTenants();
  }, [loadTenants, refreshTrigger]);

  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      loadTenants();
    }, 60000); // 60 saniye

    return () => clearInterval(interval);
  }, [autoRefresh, loadTenants]);

  const handleCreate = () => {
    navigate('/tenants/new');
  };

  const handleEdit = (id: string) => {
    navigate(`/tenants/${id}/edit`);
  };

  const handleDeleteClick = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedTenant) return;

    try {
      await tenantService.delete(selectedTenant.id);
      await loadTenants();
      setDeleteDialogOpen(false);
      setSelectedTenant(null);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Şirket grubu silinirken bir hata oluştu');
    }
  };

  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box
        sx={{
          position: 'relative',
          opacity: isRefreshing ? 0.7 : 1,
          transition: 'opacity 0.3s ease-in-out',
          pointerEvents: isRefreshing ? 'none' : 'auto',
        }}
      >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Şirket Grupları
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
        >
          Yeni Şirket Grubu
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {tenants.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', ...getRefreshPaperStyles(isRefreshing) }}>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            Henüz şirket grubu bulunmuyor.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreate}
            sx={{ mt: 2 }}
          >
            İlk Şirket Grubunu Oluştur
          </Button>
        </Paper>
      ) : (
        <Paper
          sx={{
            borderRadius: 3,
            boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.08), 0px 1px 2px rgba(0, 0, 0, 0.04)',
            overflow: 'hidden',
            ...getRefreshPaperStyles(isRefreshing),
          }}
        >
          <TableContainer sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Table size="small" sx={getRefreshTableStyles(isRefreshing)}>
              <TableHead>
                <TableRow sx={{ bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.action.hover : theme.palette.grey[50] }}>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>
                    <Box display="flex" alignItems="center" gap={0.75}>
                      <BusinessIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                      Şirket Grubu Adı
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>Açıklama</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>
                    <Box display="flex" alignItems="center" gap={0.75}>
                      <ScheduleIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                      Oluşturulma Tarihi
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }} align="right">İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tenants.map((tenant, index) => (
                  <TableRow
                    key={tenant.id}
                    hover
                    sx={{
                      bgcolor: index % 2 === 1 ? 'action.hover' : 'transparent',
                      '&:hover': { bgcolor: 'action.selected' },
                      transition: 'background-color 0.15s',
                    }}
                  >
                    <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>
                      <Box display="flex" alignItems="center" gap={0.75}>
                        <BusinessIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        {tenant.name}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>{tenant.description || '—'}</TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>
                      <Box display="flex" alignItems="center" gap={0.75}>
                        <ScheduleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        {formatDateToTR(tenant.createdAt)}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }} align="right">
                      <IconButton size="small" onClick={() => handleEdit(tenant.id)} color="primary" aria-label="Düzenle">
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDeleteClick(tenant)} color="error" aria-label="Sil">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Şirket Grubu Sil</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {selectedTenant?.name} adlı şirket grubunu silmek istediğinize emin misiniz? Bu işlem geri alınamaz.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>İptal</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Sil
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </Layout>
  );
};

export default Tenants;










